import React, { lazy, Suspense, useContext, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { Builder } from '@builder.io/sdk';
import { DamFileProvider, UseDamFileHook } from '@/contexts/DamFileProvider';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { Area, PageType, ResourceLocation } from '@/generated/data.schemas';
import { useLookupFiles } from '@/generated/image';
import { useGetLayout } from '@/generated/layout';
import { useGetSettings } from '@/generated/settings';
import { useCookieHandler } from '@/helpers/useCookieHandler';
import ApplicationDetail from '@/routes/RegistrationCompositeDetail';
import Page from '@/routes/_page.partial';
import '@/styles/index.scss';
import ErrorHandler from './ErrorHandler';

const AuthConfirmEmailLoginPage = lazy(() => import('@/routes/AuthConfirmEmailLogin'));
const AuthConfirmEmailSetPasswordPage = lazy(() => import('@/routes/AuthConfirmEmailSetPassword'));
const AuthForgottenPassword = lazy(() => import('@/routes/AuthForgottenPassword'));
const AuthResetPasswordPage = lazy(() => import('@/routes/AuthResetPassword'));
const AuthSignInPage = lazy(() => import('@/routes/AuthSignIn'));
const UserProfilePage = lazy(() => import('@/routes/UserProfile'));
const UserSettingsPage = lazy(() => import('@/routes/UserSettings'));
const BasketPage = lazy(() => import('@/routes/Basket'));
const CmsPage = lazy(() => import('@/routes/Cms'));
const NotFoundPage = lazy(() => import('@/routes/404'));
const OrderConfirmationPage = lazy(() => import('@/routes/OrderConfirmation'));
const ProductDetailPage = lazy(() => import('@/routes/ProductDetail'));
const ProductListPage = lazy(() => import('@/routes/ProductList'));
const ArticleListPage = lazy(() => import('@/routes/ArticleList'));
const AppSolutionBuilder = lazy(() => import('@/routes/AppSolutionBuilder'));
const CustomersListPage = lazy(() => import('@/routes/CustomersList'));
const CustomersDetailsPage = lazy(() => import('@/routes/CustomerDetail'));
const AuthExternalSignInCallbackPage = lazy(() => import('@/routes/AuthExternalSignInCallback'));
const LocationDetailPage = lazy(() => import('@/routes/LocationDetail'));
const EditLocationPage = lazy(() => import('@/routes/EditLocation'));
const OpportunityDetailPage = lazy(() => import('@/routes/OpportunityDetail'));

interface RedirectRulesProps extends React.PropsWithChildren {
	readonly pageType?: PageType;
}

const RedirectRules: React.FC<RedirectRulesProps> = (props) => {
	const { children, pageType } = props;
	const { segmentationId } = useContext(SegmentationContext);
	const { data: layout } = useGetLayout(segmentationId);
	const { data: settings, isLoading } = useGetSettings(segmentationId);
	// use Builder to determine if we are previewing because the page rerenders multiple times
	const isPreviewingInBuilder = Builder.isEditing || Builder.isPreviewing;

	const location = useLocation();

	if (isLoading) return null;

	const isSalesPortal = settings?.data.isSalesPortal ?? true;

	// If segment is not a sales portal, we don't need to redirect
	if (!isSalesPortal) return children;

	const staticLinks = layout?.data.staticLinks;
	const user = settings?.data.user;

	// allowed pages without login for sales portal
	const allowedPagesWithoutLogin = [
		PageType.UserSignIn,
		PageType.LoginCallback,
		PageType.UserLoginConfirmEmail,
		PageType.UserConfirmEmailSetPassword,
		PageType.UserForgotPassword,
		PageType.UserResetPassword,
	];

	switch (pageType) {
		case PageType.UserProfile:
		case PageType.UserSettings:
			if (!user) {
				return (
					<Navigate
						to={staticLinks?.find((item) => item.pageType === PageType.UserSignIn)?.url || ''}
						state={{
							area: Area.StaticPages,
							pageType: PageType.UserSignIn,
							returnTo: location.pathname,
						}}
						replace
					/>
				);
			}
			return children;
		default:
			// user must be logged in to see all pages except pageTypes in allowedPagesWithoutLogin
			if (!isPreviewingInBuilder && !allowedPagesWithoutLogin.find((p) => p === pageType) && !user) {
				return (
					<Navigate
						to={staticLinks?.find((item) => item.pageType === PageType.UserSignIn)?.url || ''}
						state={{
							area: Area.StaticPages,
							pageType: PageType.UserSignIn,
							returnTo: location.pathname,
						}}
						replace
					/>
				);
			}
			return children;
	}
};

const selectSalesPortalPage = (
	routingData: ResourceLocation | undefined,
	pageQuery: string,
): React.ReactElement<never, string | React.JSXElementConstructor<unknown>> => {
	let route: React.ReactElement | Promise<React.ReactElement>;

	switch (routingData?.area) {
		case Area.ProductCategory: {
			route = <ProductListPage productCategoryId={routingData.productCategoryId} />;
			break;
		}
		case Area.Product: {
			route = <ProductDetailPage id={routingData.productMasterId || ''} />;
			break;
		}
		case Area.StaticPages: {
			switch (routingData.pageType) {
				case PageType.Customer: {
					const urlParams = new URLSearchParams(pageQuery);
					const accountId = urlParams.get('accountid') ?? urlParams.get('accountId');
					const externalIdentifier =
						urlParams.get('externalidentifier') ?? urlParams.get('externalIdentifier');
					route = (
						<CustomersDetailsPage
							externalIdentifier={externalIdentifier ? String(externalIdentifier) : undefined}
							accountId={accountId ? Number(accountId) : undefined}
						/>
					);
					break;
				}
				case PageType.CustomersList: {
					route = <CustomersListPage />;
					break;
				}
				case PageType.LocationDetail: {
					const urlParams = new URLSearchParams(pageQuery);
					const accountId = Number(urlParams.get('accountid'));
					const locationId = Number(urlParams.get('locationid'));
					route = <LocationDetailPage accountId={accountId} locationId={locationId} />;
					break;
				}
				case PageType.EditLocation: {
					const urlParams = new URLSearchParams(pageQuery);
					const accountId = Number(urlParams.get('accountid'));
					const registrationCompositeId = Number(urlParams.get('registrationcompositeid'));
					const locationId = Number(urlParams.get('locationid'));
					const applicationId = Number(urlParams.get('applicationid'));
					route = (
						<EditLocationPage
							accountId={accountId}
							registrationCompositeId={registrationCompositeId}
							locationId={locationId}
							applicationId={applicationId}
						/>
					);
					break;
				}
				case PageType.Opportunity: {
					route = <OpportunityDetailPage />;
					break;
				}
				case PageType.Basket: {
					route = <BasketPage />;
					break;
				}
				case PageType.OrderConfirmation: {
					const urlParams = new URLSearchParams(pageQuery);
					const accountId = Number(urlParams.get('accountid'));
					const orderId = Number(urlParams.get('orderid'));
					route = <OrderConfirmationPage accountId={accountId} orderId={orderId} />;
					break;
				}
				case PageType.Search: {
					route = <ProductListPage productCategoryId={routingData.productCategoryId} />;
					break;
				}
				case PageType.UserLoginConfirmEmail: {
					route = <AuthConfirmEmailLoginPage />;
					break;
				}
				case PageType.UserConfirmEmailSetPassword: {
					route = <AuthConfirmEmailSetPasswordPage />;
					break;
				}
				case PageType.UserForgotPassword: {
					route = <AuthForgottenPassword />;
					break;
				}
				case PageType.LoginCallback: {
					route = <AuthExternalSignInCallbackPage />;
					break;
				}
				case PageType.UserResetPassword: {
					route = <AuthResetPasswordPage />;
					break;
				}
				case PageType.UserSignIn: {
					route = <AuthSignInPage />;
					break;
				}
				case PageType.NotFound: {
					route = <NotFoundPage />;
					break;
				}
				case PageType.UserProfile: {
					route = <UserProfilePage />;
					break;
				}
				case PageType.UserSettings: {
					route = <UserSettingsPage />;
					break;
				}
				case PageType.AppSolutionBuilder: {
					route = <AppSolutionBuilder />;
					break;
				}
				case PageType.ApplicationDetail:
				case PageType.RegistrationComposite: {
					const urlParams = new URLSearchParams(pageQuery);
					const accountId = Number(urlParams.get('accountid'));
					const registrationCompositeId = Number(urlParams.get('registrationcompositeid'));

					route = (
						<ApplicationDetail accountId={accountId} registrationCompositeId={registrationCompositeId} />
					);
					break;
				}
				default: {
					route = <NotFoundPage />;
				}
			}
			break;
		}
		default: {
			route = <CmsPage />;
		}
	}

	return route as React.ReactElement<never, string | React.JSXElementConstructor<unknown>>;
};

const selectPage = (
	routingData: ResourceLocation | undefined,
): React.ReactElement<never, string | React.JSXElementConstructor<unknown>> => {
	let route: React.ReactElement | Promise<React.ReactElement>;

	switch (routingData?.area) {
		case Area.StaticPages: {
			switch (routingData.pageType) {
				case PageType.Search: {
					route = <ArticleListPage />;
					break;
				}
				default: {
					route = <NotFoundPage />;
				}
			}
			break;
		}
		default: {
			route = <CmsPage />;
		}
	}

	return route as React.ReactElement<never, string | React.JSXElementConstructor<unknown>>;
};

interface AppProps {
	initialRoute?: ResourceLocation;
}

export const App: React.FunctionComponent<AppProps> = ({ initialRoute }) => {
	const location = useLocation();
	const { segmentationId } = useContext(SegmentationContext);
	const { data: settings, isLoading: isSettingsLoading } = useGetSettings(segmentationId);
	const initialMount = React.useRef(true);
	useCookieHandler();

	let routing = location.state as undefined | ResourceLocation;
	if (!routing && initialRoute) {
		if (location.pathname === initialRoute.externalRoute) {
			routing = initialRoute;
		}
	}

	const pageQuery = location.search;

	const isSalesPortal = settings?.data.isSalesPortal ?? true;

	const useDamFile: UseDamFileHook = (fileIdentifier) => {
		const result = useLookupFiles({ images: fileIdentifier === null ? [] : [fileIdentifier] });
		return result.data?.data.images.at(0)?.image ?? null;
	};

	useEffect(() => {
		if (initialMount.current) {
			initialMount.current = false;
		} else if (
			!location.state?.preventScrollTo &&
			(routing?.pageType !== PageType.AppSolutionBuilder || (!isSalesPortal && !location.hash.includes('#')))
		) {
			/* In the AppSolutionBuilder we need to maintain the scroll position to avoid frustration
			 * and in the CMS pages we want to make sure that the anchor tags work as usual,
			 * so we check for location.hash to NOT include # */
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [location, routing, isSalesPortal]);

	if (isSettingsLoading) return null;

	return (
		<ErrorHandler>
			<Suspense fallback={<Page />}>
				<RedirectRules pageType={routing?.pageType}>
					<DamFileProvider hook={useDamFile}>
						{isSalesPortal ? selectSalesPortalPage(routing, pageQuery) : selectPage(routing)}
					</DamFileProvider>
				</RedirectRules>
			</Suspense>
		</ErrorHandler>
	);
};
